(() => {
  let componentDataCache = null;
  let latestCartData = null;
  let pendingRebuyReopen = false;
  let pendingRebuyTimer = null;
  let nativeCartPatched = false;
  let nativeCartOpenPatched = false;
  let populateQueued = false;
  const REBUY_PENDING_CLASS = 'rebuy-cart--pending';
  const REBUY_CART_ROOT_SELECTORS = [
    '#rebuy-smart-cart-wrapper',
    '#rebuy-smart-cart',
    '.cart-drawer[data-rebuy-component="cart-items"]',
    '[data-smartcart-items]'
  ];

  const getRebuyRoot = () => {
    for (const sel of REBUY_CART_ROOT_SELECTORS) {
      const root = document.querySelector(sel);
      if (root) return root;
    }
    return null;
  };

  const queryRebuyAll = (selector) =>
    document.querySelectorAll(
      REBUY_CART_ROOT_SELECTORS.map(s => `${s} ${selector}`).join(', ')
    );

  const queryRebuy = (selector) =>
    document.querySelector(
      REBUY_CART_ROOT_SELECTORS.map(s => `${s} ${selector}`).join(', ')
    );

  const getDiscountInfo = (variantId, priceInPence) => {
    const cfg = window.__rebuyDiscountConfig;
    if (!cfg || cfg.magicDiscount !== 'true' || cfg.marketMonogramDiscount !== true) return null;
    if (!variantId || !cfg.marketDiscountVariantIds.includes(Number(variantId))) return null;
    const percent = parseFloat(cfg.discountByVariantId[variantId] || 0);
    if (percent <= 0) return null;
    const discounted = Math.round(priceInPence * (100 - percent) / 100);
    return { discounted, original: priceInPence, isFree: discounted <= 0 };
  };

  const formatPrice = (pence) => {
    const raw = window.Shopify?.currency?.active;
    const code = (typeof raw === 'string' && raw.length === 3) ? raw : 'GBP';
    const lang = (navigator.language || 'en').split('-')[0];
    return new Intl.NumberFormat(lang, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(pence / 100);
  };

  const loadComponentData = () => {
    const el = document.getElementById('rebuy-component-data');
    if (el) {
      try { componentDataCache = JSON.parse(el.textContent); } catch { componentDataCache = {}; }
    }
    return componentDataCache || {};
  };

  const refreshComponentData = async () => {
    try {
      const res = await fetch('/?section_id=rebuy-component-data');
      const html = await res.text();
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const script = tmp.querySelector('#rebuy-component-data');
      if (script) {
        try { componentDataCache = JSON.parse(script.textContent); } catch { componentDataCache = {}; }
        const existing = document.getElementById('rebuy-component-data');
        if (existing) existing.textContent = script.textContent;
      }
      populateImages();
      return componentDataCache || {};
    } catch {
      return componentDataCache || {};
    }
  };

  const getComponentData = (variantId) => {
    const data = componentDataCache || loadComponentData();
    return data[String(variantId)] || null;
  };

  const getItemComponentData = (itemOrVariantId) => {
    if (itemOrVariantId == null) return null;
    if (typeof itemOrVariantId === 'object') {
      return getComponentData(itemOrVariantId.variant_id || itemOrVariantId.variantId || itemOrVariantId.id);
    }
    return getComponentData(itemOrVariantId);
  };

  const hasItemComponent = (itemOrVariantId, type) => {
    const componentData = getItemComponentData(itemOrVariantId);
    return !!(componentData && componentData[type]);
  };

  const getItemCustomMetafields = (item) => {
    if (!item || typeof item !== 'object') return {};
    return item.product?.metafields?.custom || {};
  };

  const itemHasMonogramOption = (item) => {
    const custom = getItemCustomMetafields(item);
    return !!(custom.choose_monogramming || hasItemComponent(item, 'monogram'));
  };

  const itemHasWristStrapOption = (item) => {
    const custom = getItemCustomMetafields(item);
    return !!(custom.gift_wrist_strap_ || hasItemComponent(item, 'wristStrap'));
  };

  const itemHasGiftBoxOption = (item) => {
    const custom = getItemCustomMetafields(item);
    return !!(custom.gift_box_ || hasItemComponent(item, 'giftBox'));
  };

  const itemHasGiftNoteOption = (item) => {
    const custom = getItemCustomMetafields(item);
    return !!(custom.upsell_gift_note || hasItemComponent(item, 'giftNote'));
  };

  const itemHasCompactMirrorOption = (item) => {
    const custom = getItemCustomMetafields(item);
    return !!(custom.upsell_compact_mirror || hasItemComponent(item, 'compactMirror'));
  };

  const itemHasAnyFinishingTouches = (item) =>
    itemHasMonogramOption(item) ||
    itemHasWristStrapOption(item) ||
    itemHasGiftBoxOption(item) ||
    itemHasGiftNoteOption(item) ||
    itemHasCompactMirrorOption(item);

  const getItemHandle = (itemOrVariantId) => {
    if (itemOrVariantId && typeof itemOrVariantId === 'object') {
      if (itemOrVariantId.handle) return itemOrVariantId.handle;
      if (itemOrVariantId.product && itemOrVariantId.product.handle) return itemOrVariantId.product.handle;
    }
    const componentData = getItemComponentData(itemOrVariantId);
    return componentData?.handle || '';
  };

  const getCurrentCart = async () => {
    if (latestCartData) return latestCartData;
    latestCartData = await fetch('/cart.js').then(r => r.json()).catch(() => null);
    return latestCartData;
  };

  const getComponentPriceMarkup = (component) => {
    if (!component?.price) return '';

    const info = component.variantId ? getDiscountInfo(component.variantId, component.price) : null;
    if (info) {
      const label = info.isFree ? 'FREE' : `+${formatPrice(info.discounted)}`;
      return `${label} <s>${formatPrice(info.original)}</s>`;
    }

    return `+${formatPrice(component.price)}`;
  };

  const buildComponentButtonMarkup = (type, lineIndex, quantity, handle) => {
    const spinner = `
      <div class="loading-spinner">
        <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66"><use href="#icon-spinner"></use></svg>
      </div>
    `;

    if (type === 'monogram') {
      return `
        <monogram-btn class="component__add-btn" data-index="${lineIndex}" data-line-item-qty="${quantity}" data-type="line-item">
          ${spinner}
          <span>Add</span>
        </monogram-btn>
      `;
    }

    const actionMap = {
      wristStrap: 'wrist',
      giftBox: 'gift-box',
      giftNote: 'gift-note',
      compactMirror: 'compact-mirror'
    };

    const viewMap = {
      wristStrap: 'wrist-strap-drawer',
      giftBox: 'gift-box-drawer',
      giftNote: 'gift-note-drawer',
      compactMirror: 'compact-mirror-drawer'
    };

    const action = actionMap[type];
    const view = viewMap[type];
    if (!action || !view || !handle) return '';

    return `
      <component-btn class="component__add-btn" data-index="${lineIndex}" data-quantity="${quantity}" data-action="${action}" data-product-url="/products/${handle}" data-drawer-url="/products/${handle}?view=${view}" data-product-handle="${handle}">
        ${spinner}
        <span>Add</span>
      </component-btn>
    `;
  };

  const buildComponentCardMarkup = (type, component, lineIndex, quantity, handle) => {
    if (!component) return '';
    if (type !== 'monogram' && component.available === false) return '';

    const config = {
      monogram: {
        fallbackTitle: 'Personalisation',
        metaHtml: '<div class="component__meta">Initials: Up to three letters</div>'
      },
      wristStrap: {
        fallbackTitle: 'Wrist Strap',
        metaHtml: `<div class="component__meta"${component.meta ? '' : ' hidden'}>${component.meta || ''}</div>`
      },
      giftBox: {
        fallbackTitle: 'Gift Box',
        metaHtml: `<div class="component__meta"${component.rte ? '' : ' hidden'}>${component.rte || ''}</div>`
      },
      giftNote: {
        fallbackTitle: 'Gift Note',
        metaHtml: '<div class="component__meta" hidden></div>'
      },
      compactMirror: {
        fallbackTitle: 'Compact Mirror',
        metaHtml: '<div class="component__meta" hidden></div>'
      }
    }[type];

    if (!config) return '';

    const title = component.title || config.fallbackTitle;
    const buttonMarkup = buildComponentButtonMarkup(type, lineIndex, quantity, handle);
    if (!buttonMarkup) return '';

    return `
      <div class="component__product">
        <div class="component__alpha">
          <div class="component__img" data-component-type="${type}">
            <img src="${component.image || ''}" alt="${title}">
          </div>
          <div class="component__rte">
            <div class="component__header">
              <h5 class="component__product-title" data-component-title>${title}</h5>
              <div class="component__product-price" data-component-price>${getComponentPriceMarkup(component)}</div>
            </div>
            ${config.metaHtml}
          </div>
        </div>
        <div class="component__beta">
          ${buttonMarkup}
        </div>
      </div>
    `;
  };

  const ensureFallbackFinishingTouches = async () => {
    const cart = await getCurrentCart();
    if (!cart?.items?.length) return;

    const rows = queryRebuyAll('.cart-item.rebuy-cart__flyout-item');
    rows.forEach((row, rowIndex) => {
      const cartItem = cart.items[rowIndex];
      if (!cartItem) return;

      const componentData = getComponentData(cartItem.variant_id);
      if (!componentData) return;

      const hasMonogram = !!componentData.monogram && !cartItem.properties?._isMonogram_dev;
      const hasWrist = !!componentData.wristStrap && !cartItem.properties?._isWristStrap_dev;
      const hasGiftBox = !!componentData.giftBox && !cartItem.properties?._isGiftBox_dev;
      const hasGiftNote = !!componentData.giftNote && !cartItem.properties?._isGiftNote_dev;
      const hasCompactMirror = !!componentData.compactMirror;
      const shouldRender = hasMonogram || hasWrist || hasGiftBox || hasGiftNote || hasCompactMirror;

      const existingPanel = row.querySelector('.components-td:not(.line-components)');
      const fallbackPanel = row.querySelector('.components-td[data-es-fallback-finishing-touches="true"]');

      if (!shouldRender) {
        if (fallbackPanel) fallbackPanel.remove();
        return;
      }

      if (existingPanel && !fallbackPanel) {
        const hasCards = existingPanel.querySelector('.component__product');
        if (hasCards) return;
        existingPanel.remove();
      }

      const handle = componentData.handle || getItemHandle(cartItem);
      const lineIndex = rowIndex + 1;
      const quantity = cartItem.quantity || 1;
      const cards = [
        hasMonogram ? buildComponentCardMarkup('monogram', componentData.monogram, lineIndex, quantity, handle) : '',
        hasWrist ? buildComponentCardMarkup('wristStrap', componentData.wristStrap, lineIndex, quantity, handle) : '',
        hasGiftBox ? buildComponentCardMarkup('giftBox', componentData.giftBox, lineIndex, quantity, handle) : '',
        hasGiftNote ? buildComponentCardMarkup('giftNote', componentData.giftNote, lineIndex, quantity, handle) : '',
        hasCompactMirror ? buildComponentCardMarkup('compactMirror', componentData.compactMirror, lineIndex, quantity, handle) : ''
      ].filter(Boolean).join('');

      if (!cards) {
        if (fallbackPanel) fallbackPanel.remove();
        return;
      }

      const markup = `
        <div class="components-td" data-variant-id="${cartItem.variant_id}" data-es-fallback-finishing-touches="true">
          <details>
            <summary>
              <span class="details__label">Finishing Touches</span>
              <div class="chevron__icon">
                <svg width="19" height="11" viewBox="0 0 19 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9.25042 7.48393L2.63462 0.867239L2.6346 0.867215C2.4001 0.632716 2.08205 0.500977 1.75042 0.500977C1.41879 0.500977 1.10074 0.632716 0.866239 0.867215C0.63174 1.10171 0.5 1.41976 0.5 1.75139C0.5 2.08302 0.63174 2.40107 0.866239 2.63557L8.36604 10.1354L8.36624 10.1356L8.71979 9.78202L9.25042 7.48393ZM9.25042 7.48393L15.8662 0.867239L15.8662 0.867214C15.9824 0.751102 16.1202 0.658998 16.2719 0.596159C16.4236 0.533319 16.5862 0.500977 16.7504 0.500977C16.9146 0.500977 17.0772 0.533319 17.2289 0.596159C17.3806 0.658997 17.5185 0.751102 17.6346 0.867214L17.6346 0.867219C17.7507 0.98333 17.8428 1.12117 17.9057 1.27288C17.9685 1.42458 18.0008 1.58718 18.0008 1.75139C18.0008 1.9156 17.9685 2.0782 17.9057 2.22991C17.8428 2.38161 17.7507 2.51946 17.6346 2.63557L17.281 2.28202L17.6346 2.63557L10.1348 10.1354M9.25042 7.48393L10.1348 10.1354M10.1348 10.1354L10.1346 10.1356L9.78104 9.78202L10.1348 10.1354Z" fill="black" stroke="black"></path>
                </svg>
              </div>
            </summary>
            <div class="components-panel">${cards}</div>
          </details>
        </div>
      `;

      if (fallbackPanel) {
        fallbackPanel.outerHTML = markup;
      } else {
        row.insertAdjacentHTML('beforeend', markup);
      }
    });
  };

  const populateImages = () => {
    const els = queryRebuyAll('.component__img[data-component-type]');
    els.forEach(el => {
      const row = el.closest('[data-variant-id]');
      if (!row) return;
      const comp = (getComponentData(row.dataset.variantId) || {})[el.dataset.componentType];
      
      if (!comp?.image) return;

      const img = el.querySelector('img');
      if (img) {
        if (img.getAttribute('src') !== comp.image) img.setAttribute('src', comp.image);
        if (comp.title && img.getAttribute('alt') !== comp.title) img.setAttribute('alt', comp.title);
        img.loading = 'lazy';
      }

      const card = el.closest('.line-component, .component__product');
      if (!card) return;

      const titleEl = card.querySelector('[data-component-title]');
      if (titleEl) {
        const titleVal = comp.title || '';
        if (titleEl.textContent !== titleVal) titleEl.textContent = titleVal;
        titleEl.hidden = !comp.title;
      }

      const metaEl = card.querySelector('[data-component-meta]');
      if (metaEl) {
        const metaVal = comp.meta || '';
        if (metaEl.textContent !== metaVal) metaEl.textContent = metaVal;
        metaEl.hidden = !comp.meta;
      }

      const rteEl = card.querySelector('[data-component-rte]');
      if (rteEl) {
        const rteVal = comp.rte || '';
        if (rteEl.innerHTML !== rteVal) rteEl.innerHTML = rteVal;
        rteEl.hidden = !comp.rte;
      }

      const priceEl = card.querySelector('[data-component-price]');
      const compareEl = card.querySelector('[data-component-price-compare]');
      if (priceEl) {
        const info = comp.variantId ? getDiscountInfo(comp.variantId, comp.price) : null;

        let priceVal = comp.priceFormatted || '';
        if (!priceVal && comp.price) priceVal = `+${formatPrice(comp.price)}`;

        if (info) {
          const label = info.isFree ? 'FREE' : `+${formatPrice(info.discounted)}`;
          const fullHtml = `${label} <s>${formatPrice(info.original)}</s>`;
          if (priceEl.innerHTML !== fullHtml) priceEl.innerHTML = fullHtml;
          priceEl.hidden = false;
        } else {
          if (priceEl.textContent !== priceVal) priceEl.textContent = priceVal;
          priceEl.hidden = !priceVal;
        }
        if (compareEl) compareEl.hidden = true;
      }
    });
  };

  const populateLinePrices = () => {
    const els = queryRebuyAll('[data-line-charges]');
    els.forEach(el => {
      const amount = parseFloat(el.dataset.chargesAmount || 0);
      const variantId = el.dataset.chargesVariant;
      if (!amount) return;

      const pence = Math.round(amount * 100);
      const info = variantId ? getDiscountInfo(variantId, pence) : null;

      if (info) {
        const label = info.isFree ? 'FREE' : `+${formatPrice(info.discounted)}`;
        const html = `${label} <s>${formatPrice(info.original)}</s>`;
        if (el.innerHTML !== html) el.innerHTML = html;
      } else {
        const text = `+${formatPrice(pence)}`;
        if (el.textContent !== text) el.textContent = text;
      }
    });
  };

  const schedulePopulateImages = () => {
    if (populateQueued) return;
    populateQueued = true;
    requestAnimationFrame(() => {
      populateQueued = false;
      populateImages();
      populateLinePrices();
      ensureFallbackFinishingTouches();
    });
  };

  const setPendingRebuyReopen = (value) => {
    pendingRebuyReopen = value;
    if (pendingRebuyTimer) {
      clearTimeout(pendingRebuyTimer);
      pendingRebuyTimer = null;
    }
    if (value) {
      pendingRebuyTimer = setTimeout(() => {
        pendingRebuyReopen = false;
        pendingRebuyTimer = null;
        setRebuyPendingState(false);
      }, 5000);
    }
  };

  const setRebuyPendingState = (value) => {
    const root = getRebuyRoot();
    if (!root) return;
    root.classList.toggle(REBUY_PENDING_CLASS, !!value);
  };

  const hideAddonDrawers = () => {
    for (const selector of ['monogram-drawer', 'gift-note-drawer', 'gift-box-drawer', 'wrist-strap-drawer', 'compact-mirror-drawer']) {
      const drawer = document.querySelector(selector);
      if (!drawer) continue;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
    }
    document.body.classList.remove('overflow-hidden');
  };

  const closeRebuyCart = () => {
    try {
      const closeBtn = queryRebuy('.close-btn, [data-rebuy-cart-close]');
      if (closeBtn) { closeBtn.click(); return true; }
      if (window.Rebuy?.SmartCart?.hide) {
        window.Rebuy.SmartCart.hide();
        return true;
      }
    } catch {}
    return false;
  };

  const updateCartBubble = async () => {
    const cart = await fetch('/cart.js').then(r => r.json()).catch(() => null);
    if (!cart) return;
    latestCartData = cart;
    const bubble = document.querySelector('.cart-count-bubble');
    if (!bubble) return;
    const countEl = bubble.querySelector('[aria-hidden="true"]');
    const srEl = bubble.querySelector('.visually-hidden');
    if (countEl) countEl.textContent = cart.item_count;
    if (srEl) srEl.textContent = `${cart.item_count} item${cart.item_count === 1 ? '' : 's'}`;
  };

  const closeNativeCart = () => {
    try {
      const d = document.querySelector('cart-drawer');
      if (d && typeof d.close === 'function') d.close();
    } catch {}
  };

  const openRebuyCart = () => {
    try {
      window.Rebuy?.SmartCart?.show?.();
    } catch {}
  };

  const openRebuyCartLoading = () => {
    setPendingRebuyReopen(true);
    setRebuyPendingState(true);
    hideAddonDrawers();
    closeNativeCart();
    openRebuyCart();
  };

const syncRebuyCart = async (options = {}) => {
  await refreshComponentData();
  latestCartData = await fetch('/cart.js').then(r => r.json()).catch(() => latestCartData);

  try {
    window.Rebuy?.SmartCart?.setCart?.(null);
  } catch {}

  if (!options.keepPending) setRebuyPendingState(false);

  if (options.reopen) {
    setTimeout(() => {
      closeNativeCart();
      openRebuyCart();

      setTimeout(() => {
        schedulePopulateImages();
        updateCartBubble();
      }, 500);
    }, options.delay || 250);
  } else {
    setTimeout(() => {
      schedulePopulateImages();
      updateCartBubble();
    }, 500);
  }
};

  const patchNativeCartRender = () => {
    if (nativeCartPatched) return;
    const Ctor = window.customElements?.get('cart-drawer');
    if (!Ctor?.prototype?.renderContents || typeof Ctor.prototype.renderContents !== 'function') {
      setTimeout(patchNativeCartRender, 500);
      return;
    }
    const orig = Ctor.prototype.renderContents;
    Ctor.prototype.renderContents = function (...args) {
      const result = orig.apply(this, args);
      if (pendingRebuyReopen) {
        setPendingRebuyReopen(false);
        syncRebuyCart({ reopen: true, delay: 150 });
      }
      return result;
    };
    nativeCartPatched = true;
  };

  const patchNativeCartOpen = () => {
    if (nativeCartOpenPatched) return;
    const Ctor = window.customElements?.get('cart-drawer');
    if (!Ctor?.prototype?.open || typeof Ctor.prototype.open !== 'function') {
      setTimeout(patchNativeCartOpen, 500);
      return;
    }
    Ctor.prototype.open = function () {
      openRebuyCart();
    };
    nativeCartOpenPatched = true;
  };

  const getOrCreateMonogramAnchor = () => {
    let anchor = document.getElementById('rebuy-monogram-anchor');
    if (!anchor) {
      anchor = document.createElement('div');
      anchor.id = 'rebuy-monogram-anchor';
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
    }
    return anchor;
  };

  const prepareMonogramDrawer = async (btn) => {
    const row = btn.closest('[data-variant-id]');
    const variantId = row?.dataset.variantId ?? null;
    const lineIndex = parseInt(btn.dataset.index);

    const openDrawer = async (info) => {
      if (!info?.monogramJson) { btn.classList.remove('is-loading'); return; }

      try {
        const cart = await fetch('/cart.js').then(r => r.json());
        const item = cart.items[lineIndex - 1];
        const monogramData = structuredClone(info.monogramJson);

        if (item) {
          monogramData.monogram_image = item.image || monogramData.monogram_image;
          monogramData.monogram_id = item.variant_id;
          monogramData.line_item_price = item.price;
        }

        const anchor = getOrCreateMonogramAnchor();
        const fakeBtn = anchor.querySelector('monogram-btn') || document.createElement('monogram-btn');
        fakeBtn.setAttribute('data-type', 'line-item');
        fakeBtn.setAttribute('data-index', lineIndex);
        fakeBtn.setAttribute('data-line-item-qty', item ? item.quantity : 1);
        anchor.innerHTML = '';
        anchor.appendChild(fakeBtn);

        const script = document.createElement('script');
        script.id = 'monogramJsonData';
        script.type = 'application/json';
        script.textContent = JSON.stringify(monogramData);
        fakeBtn.insertAdjacentElement('afterend', script);

        const monogramDrawer = document.querySelector('monogram-drawer');
        if (!monogramDrawer) return;

        const savedProps = item
          ? Object.fromEntries(Object.entries(item.properties).filter(([_, v]) => v !== ''))
          : {};
        closeRebuyCart();

        setTimeout(() => {
          monogramDrawer.open({ currentTarget: fakeBtn });
          monogramDrawer.lineProperties = savedProps;
          btn.classList.remove('is-loading');
        }, 150);
      } catch {
        btn.classList.remove('is-loading');
      }
    };

    let info = variantId ? getComponentData(variantId) : null;
    if (info?.monogramJson) { openDrawer(info); return; }

    try {
      await refreshComponentData();
      openDrawer(variantId ? getComponentData(variantId) : null);
    } catch {
      btn.classList.remove('is-loading');
    }
  };

  const handleAddButtonClick = (e) => {
    const btn = e.target.closest('monogram-btn, component-btn');
    if (!btn) return;
    if (!btn.closest(REBUY_CART_ROOT_SELECTORS.join(', '))) return;

    btn.classList.add('is-loading');

    if (btn.tagName.toLowerCase() === 'monogram-btn') {
      e.stopPropagation();
      e.preventDefault();
      requestAnimationFrame(() => prepareMonogramDrawer(btn));
      return;
    }

    requestAnimationFrame(() => {
      closeRebuyCart();
      setTimeout(() => btn.classList.remove('is-loading'), 300);
    });
  };

  document.addEventListener('click', handleAddButtonClick, true);

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[js-monogram-submit-btn], [data-gift-note-add], [data-gift-box-add], [data-wrist-strap-add], [data-compact-mirror-add]');
    if (!btn || btn.disabled || btn.getAttribute('disabled') !== null || btn.getAttribute('aria-disabled') === 'true') return;
    if (!btn.matches('[js-monogram-submit-btn]')) {
      const drawer = btn.closest('wrist-strap-drawer, gift-box-drawer, gift-note-drawer, compact-mirror-drawer');
      if (!drawer || !drawer.cartContext) return;
    }
    openRebuyCartLoading();
  }, true);

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[js-gift-note-atc-btn], [js-atc-btn], .gift-note-atc-btn');
  if (!btn) return;

  setTimeout(async () => {
    await syncRebuyCart({ reopen: true, delay: 300 });

    setTimeout(() => {
      schedulePopulateImages();
    }, 500);

    setTimeout(() => {
      schedulePopulateImages();
    }, 1000);
  }, 1200);
}, true);

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-rebuy-cart-upsell-atc]');
  if (!btn) return;
  if (btn.disabled || btn.getAttribute('disabled') !== null || btn.getAttribute('aria-disabled') === 'true') return;

  setTimeout(async () => {
    await syncRebuyCart({ reopen: true, delay: 300 });

    setTimeout(() => {
      schedulePopulateImages();
    }, 500);

    setTimeout(() => {
      schedulePopulateImages();
    }, 1000);
  }, 1200);
}, true);

  for (const evt of [
    'monogram-drawer:closed', 'wrist-strap-drawer:closed', 'gift-box-drawer:closed',
    'gift-note-drawer:closed', 'monogram:added', 'wrist-strap:added',
    'gift-box:added', 'gift-note:added', 'compact-mirror:added'
  ]) {
    document.addEventListener(evt, () => syncRebuyCart({ reopen: true, delay: 300 }));
  }

  document.addEventListener('rebuy:cart.change', () => {
    updateCartBubble();
    latestCartData = null;
    refreshComponentData().then(() => {
      schedulePopulateImages();
      setTimeout(schedulePopulateImages, 600);
      setTimeout(schedulePopulateImages, 1200);
    });
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (pendingRebuyReopen) {
        setPendingRebuyReopen(false);
        syncRebuyCart({ reopen: true, delay: 150 });
      } else {
        syncRebuyCart();
      }
    });
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.addedNodes.length && queryRebuy('.component__img[data-component-type]')) {
        schedulePopulateImages();
        break;
      }
    }
  });

  const startObserving = () => {
    const target = getRebuyRoot();
    if (target) {
      observer.observe(target, { childList: true, subtree: true });
      schedulePopulateImages();
    } else {
      setTimeout(startObserving, 1000);
    }
  };

  const interceptCartIcon = () => {
    document.addEventListener('click', (e) => {
      const link = e.target.closest('#cart-icon-bubble, a[href*="/cart"]');
      if (!link) return;
      if (link.closest(REBUY_CART_ROOT_SELECTORS.join(', '))) return;
      if (link.closest('form[action*="/cart"]')) return;
      e.preventDefault();
      e.stopPropagation();
      openRebuyCart();
    }, true);
  };

  const init = () => {
    loadComponentData();
    startObserving();
    patchNativeCartOpen();
    patchNativeCartRender();
    interceptCartIcon();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }


  const MONOGRAM_KEYS_TO_REMOVE = [
    '_isMonogram',
    '_isMonogram_dev',
    '_monogramCharges',
    '_mgLetters',
    '_mgId',
    '_mono_preview_image',
    '_isMonogramDiscount',
    '_monogramDiscount',
  ];

  window.RebuyFinishingTouches = {
    getComponentData,
    getItemComponentData,
    hasItemComponent,
    getItemCustomMetafields,
    itemHasMonogramOption,
    itemHasWristStrapOption,
    itemHasGiftBoxOption,
    itemHasGiftNoteOption,
    itemHasCompactMirrorOption,
    itemHasAnyFinishingTouches,
    getItemHandle,
    refreshComponentData,
    populateImages,
    openRebuyCart,
    openRebuyCartLoading,

    async removeComponent(btn) {
      const index = parseInt(btn.dataset.index);
      const { action } = btn.dataset;
      if (!index || isNaN(index) || !action) return;
      btn.classList.add('is-loading');

      const keysToRemove = {
        wrist: ['_isWristStrap_dev', '_wristStrapCharges', '_wristStrapColor', '_wristId', '_isStrapDiscount', '_strapDiscount'],
        'gift-box': ['_isGiftBox_dev', '_giftBoxCharges', '_giftBoxId', '_isGiftDiscount', '_giftDiscount'],
        'gift-note': ['_isGiftNote_dev', '_giftNoteCharges', '_giftNoteMessage', '_giftNoteEmptyMessage', '_giftNoteId']
      }[action] || [];

      try {
        const cart = await fetch('/cart.js').then(r => r.json());
        const item = cart.items[index - 1];
        if (!item) return;
        const newProps = Object.fromEntries(
          Object.entries(item.properties)
            .filter(([key, v]) => v !== '' && !keysToRemove.includes(key))
        );
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: index, properties: newProps })
        }).then(r => r.json());
        syncRebuyCart();
      } finally {
        btn.classList.remove('is-loading');
      }
    },

    async removeMonogram(btn) {
      const index = parseInt(btn.dataset.index);
      if (!index || isNaN(index)) return;
      btn.classList.add('is-loading');

      try {
        const cart = await fetch('/cart.js').then(r => r.json());
        const item = cart.items[index - 1];
        if (!item) return;
        const newProps = Object.fromEntries(
          Object.entries(item.properties).filter(([key, v]) => {
            if (v === '') return false;
            if (key.startsWith('Add up to')) return false;
            if (MONOGRAM_KEYS_TO_REMOVE.includes(key)) return false;
            return true;
          })
        );
        newProps._mgblock = 'false';
        const changePayload = {
          quantity: item.quantity,
          properties: newProps
        };

        if (item.key) {
          changePayload.id = item.key;
        } else {
          changePayload.line = index;
        }

        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(changePayload)
        });

        if (!response.ok) {
          throw new Error(`Unable to remove monogram: ${response.status}`);
        }

        await response.json();
        await syncRebuyCart();
      } finally {
        btn.classList.remove('is-loading');
      }
    }
  };
})();
